import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const API_KEY = import.meta.env.VITE_API_KEY || '';

async function apiFetch<T = unknown>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${API_KEY}`,
      ...options?.headers,
    },
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

// ---- Types ----

export interface QueueItem {
  id: string;
  from_email: string;
  from_name: string;
  subject: string;
  body_preview: string;
  category: string;
  confidence: number;
  escalation_flag: boolean;
  escalation_reason?: string;
  draft_response: string;
  received_at: string;
  status: string;
}

export interface HistoryItem {
  id: string;
  from_email: string;
  from_name: string;
  subject: string;
  category: string;
  confidence: number;
  action: 'sent' | 'auto' | 'edited' | 'rejected' | 'escalated';
  processed_at: string;
}

export interface Metrics {
  total_received: number;
  auto_handled_pct: number;
  avg_response_time_min: number;
  accuracy_pct: number;
  by_category: Record<string, { count: number; auto: number; manual: number }>;
}

export interface Settings {
  kill_switch: boolean;
  categories: Record<string, { auto_send: boolean; threshold: number }>;
  daily_cap: number;
  hourly_cap: number;
  circuit_breaker_limit: number;
  disclosure_text: string;
}

export interface Escalation {
  id: string;
  email_id: string;
  reason: string;
  from_email: string;
  subject: string;
  created_at: string;
  resolved: boolean;
}

export interface QueueFilters {
  category?: string;
  min_confidence?: number;
  max_confidence?: number;
}

export interface HistoryParams {
  page?: number;
  per_page?: number;
  search?: string;
  category?: string;
  date_from?: string;
  date_to?: string;
}

export interface DateRange {
  from?: string;
  to?: string;
}

// ---- Helpers ----

function toQueryString(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

// ---- Query Hooks ----

export function useQueue(filters?: QueueFilters) {
  return useQuery<QueueItem[]>({
    queryKey: ['queue', filters],
    queryFn: () =>
      apiFetch<QueueItem[]>(
        `/queue${toQueryString({
          category: filters?.category,
          min_confidence: filters?.min_confidence,
          max_confidence: filters?.max_confidence,
        })}`,
      ),
    refetchInterval: 15_000,
  });
}

export function useHistory(params?: HistoryParams) {
  return useQuery<{ items: HistoryItem[]; total: number; page: number; per_page: number }>({
    queryKey: ['history', params],
    queryFn: () =>
      apiFetch(
        `/history${toQueryString({
          page: params?.page,
          per_page: params?.per_page,
          search: params?.search,
          category: params?.category,
          date_from: params?.date_from,
          date_to: params?.date_to,
        })}`,
      ),
  });
}

export function useMetrics(dateRange?: DateRange) {
  return useQuery<Metrics>({
    queryKey: ['metrics', dateRange],
    queryFn: () =>
      apiFetch<Metrics>(
        `/metrics${toQueryString({ from: dateRange?.from, to: dateRange?.to })}`,
      ),
  });
}

export function useSettings() {
  return useQuery<Settings>({
    queryKey: ['settings'],
    queryFn: () => apiFetch<Settings>('/settings'),
  });
}

export function useEscalations() {
  return useQuery<Escalation[]>({
    queryKey: ['escalations'],
    queryFn: () => apiFetch<Escalation[]>('/escalations'),
    refetchInterval: 30_000,
  });
}

// ---- Mutation Hooks ----

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (settings: Partial<Settings>) =>
      apiFetch('/settings', { method: 'PUT', body: JSON.stringify(settings) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });
}

export function useKillSwitch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      apiFetch('/settings/killswitch', {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });
}

export function useReviewAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, edited_response }: { id: string; action: 'approve' | 'reject' | 'edit'; edited_response?: string }) =>
      apiFetch(`/queue/${id}/review`, {
        method: 'POST',
        body: JSON.stringify({ action, edited_response }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['queue'] });
      qc.invalidateQueries({ queryKey: ['history'] });
      qc.invalidateQueries({ queryKey: ['metrics'] });
    },
  });
}

export function useResolveEscalation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/escalations/${id}/resolve`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['escalations'] }),
  });
}
